document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

class SynaNavigation {
  constructor(root = document) {
    this.root = root;
    this.header = root.querySelector('[data-syna-header]');
    this.drawer = document.getElementById('MobileMenu');
    this.activeDrawer = null;
    this.overlay = document.querySelector('.syna-drawer-overlay');
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.lastFocus = null;
    this.closeTimer = null;
    this.init();
  }

  init() {
    if (!this.header) return;
    this.header.closest('.shopify-section')?.classList.add('syna-header-section');
    const hero = document.querySelector('[data-syna-hero]');
    this.header.dataset.heroMode = hero?.dataset.headerMode || 'light';
    this.updateHeader();
    window.addEventListener('scroll', () => this.updateHeader(), { passive: true, signal: this.signal });

    this.root.querySelectorAll('[data-mega-item]').forEach((item) => {
      const trigger = item.querySelector('.syna-mega-trigger');
      if (!trigger) return;
      trigger.addEventListener('click', () => this.toggleMega(item), { signal: this.signal });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openMega(item);
          item.querySelector('[data-mega-panel] a')?.focus();
        }
      }, { signal: this.signal });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeMega(item, true);
      }, { signal: this.signal });
      if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          this.openMega(item);
        }, { signal: this.signal });
        item.addEventListener('mouseleave', () => {
          this.closeTimer = setTimeout(() => this.closeMega(item), 160);
        }, { signal: this.signal });
      }
    });

    this.root.querySelectorAll('[data-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(button), { signal: this.signal });
    });
    this.drawer?.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        // Close only unrelated sections. The drawer nests group accordions
        // inside section accordions, so closing every other open <details>
        // would close this one's own parent and collapse the panel the
        // moment a group was tapped.
        this.drawer.querySelectorAll('details[open]').forEach((other) => {
          if (other === details) return;
          if (other.contains(details) || details.contains(other)) return;
          other.open = false;
        });
      }, { signal: this.signal });
    });
    document.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-mega-item]')) this.closeAllMegas();
    }, { signal: this.signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (this.activeDrawer?.classList.contains('is-open')) this.closeDrawer();
        else this.closeAllMegas(true);
      }
      if (event.key === 'Tab' && this.activeDrawer?.classList.contains('is-open')) this.trapFocus(event);
    }, { signal: this.signal });
  }

  updateHeader() {
    this.header?.classList.toggle('is-scrolled', window.scrollY > 24);
  }

  toggleMega(item) {
    item.classList.contains('is-open') ? this.closeMega(item, true) : this.openMega(item);
  }

  openMega(item) {
    this.root.querySelectorAll('[data-mega-item].is-open').forEach((other) => {
      if (other !== item) this.closeMega(other);
    });
    item.classList.add('is-open');
    item.querySelector('.syna-mega-trigger')?.setAttribute('aria-expanded', 'true');
    this.header.classList.add('is-mega-open');
  }

  closeMega(item, returnFocus = false) {
    if (!item) return;
    item.classList.remove('is-open');
    const trigger = item.querySelector('.syna-mega-trigger');
    trigger?.setAttribute('aria-expanded', 'false');
    if (!this.root.querySelector('[data-mega-item].is-open')) this.header?.classList.remove('is-mega-open');
    if (returnFocus) trigger?.focus();
  }

  closeAllMegas(returnFocus = false) {
    const open = this.root.querySelector('[data-mega-item].is-open');
    this.root.querySelectorAll('[data-mega-item].is-open').forEach((item) => this.closeMega(item));
    if (returnFocus) open?.querySelector('.syna-mega-trigger')?.focus();
  }

  openDrawer(trigger) {
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer) return;
    this.closeAllMegas();
    this.lastFocus = trigger;
    this.activeDrawer = drawer;
    drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    // Force a reflow so the slide-in still animates, then set the open state
    // synchronously. Deferring this to requestAnimationFrame meant a dropped
    // or throttled frame left the drawer parked off-screen while the overlay
    // was already covering the page — every tap hit the overlay and the menu
    // appeared completely dead.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.body.classList.add('drawer-open');
    trigger.setAttribute('aria-expanded', 'true');
    (drawer.querySelector('[data-drawer-initial-focus]') || drawer.querySelector('button, a, summary, input'))?.focus();
  }

  closeDrawer() {
    const drawer = this.activeDrawer;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    this.lastFocus?.setAttribute('aria-expanded', 'false');
    const finish = () => {
      drawer.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
      this.lastFocus?.focus();
      this.activeDrawer = null;
    };
    matchMedia('(prefers-reduced-motion: reduce)').matches ? finish() : setTimeout(finish, 270);
  }

  trapFocus(event) {
    const focusable = [...this.activeDrawer.querySelectorAll('a,button,summary,input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter((element) => !element.disabled && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  destroy() {
    clearTimeout(this.closeTimer);
    this.controller.abort();
  }
}

class SynaProductZoom {
  constructor() {
    document.querySelectorAll('[data-zoom-src]:not([data-zoom-ready])').forEach((button) => {
      button.dataset.zoomReady = 'true';
      button.addEventListener('click', () => {
      const dialog = document.getElementById('ProductZoom');
      const target = dialog?.querySelector('[data-zoom-target]');
      if (!dialog || !target) return;
      let image = target.querySelector('img');
      if (!image) {
        image = document.createElement('img');
        Object.assign(image, { className: 'zoom-image', width: 1600, height: 2000, alt: '' });
        target.append(image);
      }
      image.src = button.dataset.zoomSrc;
      image.alt = button.dataset.zoomAlt || '';
      dialog.showModal();
      });
    });
    document.querySelectorAll('[data-dialog-close]:not([data-close-ready])').forEach((button) => {
      button.dataset.closeReady = 'true';
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
  }
}

class SynaHomepage {
  constructor() {
    this.observer = null;
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.initReveals();
    this.initStories();
    this.initVideos();
  }

  initReveals() {
    const elements = [...document.querySelectorAll('[data-syna-reveal]')];
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    elements.forEach((element) => element.classList.add('syna-reveal-ready'));
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        this.observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((element) => this.observer.observe(element));
  }

  initStories() {
    document.querySelectorAll('[data-story-section]').forEach((section) => {
      const slides = [...section.querySelectorAll('[data-story-slide]')];
      const status = section.querySelector('[data-story-status]');
      let index = 0;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });
        if (status) status.textContent = `${index + 1} / ${slides.length}`;
      };
      section.querySelector('[data-story-prev]')?.addEventListener('click', () => show(index - 1), { signal: this.signal });
      section.querySelector('[data-story-next]')?.addEventListener('click', () => show(index + 1), { signal: this.signal });
    });
  }

  initVideos() {
    if (!('IntersectionObserver' in window)) return;
    const videos = [...document.querySelectorAll('.syna-craft video')];
    const videoObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !entry.isIntersecting) entry.target.pause();
      else entry.target.play().catch(() => {});
    }), { threshold: 0.15 });
    videos.forEach((video) => videoObserver.observe(video));
    this.videoObserver = videoObserver;
  }

  destroy() {
    this.controller.abort();
    this.observer?.disconnect();
    this.videoObserver?.disconnect();
  }
}

let synaNavigation;
let synaHomepage;
const initialiseSyna = () => {
  synaNavigation?.destroy();
  synaHomepage?.destroy();
  synaNavigation = new SynaNavigation();
  synaHomepage = new SynaHomepage();
  new SynaProductZoom();
};

initialiseSyna();
document.addEventListener('shopify:section:load', initialiseSyna);
document.addEventListener('shopify:section:unload', () => {
  synaNavigation?.destroy();
  synaHomepage?.destroy();
});
document.addEventListener('shopify:block:select', (event) => {
  const item = event.target.closest?.('[data-mega-item]');
  if (item) synaNavigation?.openMega(item);
  const selectedStory = document.querySelector(`[data-story-block="${event.detail?.blockId}"]`);
  if (selectedStory) {
    selectedStory.closest('[data-story-section]')?.querySelectorAll('[data-story-slide]').forEach((slide) => {
      slide.hidden = slide !== selectedStory;
      slide.classList.toggle('is-active', slide === selectedStory);
    });
  }
});

/* Hero video: progressive enhancement only.
   The markup ships with no src, so nothing is fetched until this decides
   the visit can afford it. Mobile, reduced motion, save-data and slow
   connections all keep the still image and download zero video bytes. */
(function () {
  const video = document.querySelector('[data-syna-hero-video]');
  if (!video) return;

  const src = video.dataset.videoSrc;
  if (!src) return;

  const wide = window.matchMedia('(min-width: 750px)');
  const motionOK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const connectionOK = () => {
    const c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    return !/(^|-)(slow-2g|2g|3g)$/.test(c.effectiveType || '');
  };

  let started = false;
  const start = () => {
    if (started || !wide.matches || !motionOK || !connectionOK()) return;
    started = true;
    video.src = src;
    video.load();
    const played = video.play();
    if (played) {
      played
        .then(() => video.classList.add('is-playing'))
        .catch(() => {
          // Autoplay refused. Leave the still image in place.
          video.removeAttribute('src');
          video.load();
          started = false;
        });
    }
  };

  // Wait for idle so the video never competes with the hero image.
  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 2500 });
  } else {
    window.addEventListener('load', () => setTimeout(start, 400), { once: true });
  }

  wide.addEventListener('change', (event) => {
    if (event.matches) {
      start();
    } else if (started) {
      video.pause();
      video.classList.remove('is-playing');
    }
  });
})();

/* Enquiry pre-fill.
   A product page in enquiry mode links here with ?piece=&handle=&ref=.
   Carry that into the form so the enquiry arrives identifying the piece,
   and say so on screen rather than filling a field the visitor cannot see. */
(function () {
  const field = document.querySelector('[data-piece-field]');
  if (!field) return;

  const params = new URLSearchParams(window.location.search);
  const piece = (params.get('piece') || '').trim();
  if (!piece) return;

  const handle = (params.get('handle') || '').trim();
  const ref = (params.get('ref') || '').trim();

  field.value = ref ? `${piece} (ref ${ref})` : piece;

  const note = document.querySelector('[data-piece-note]');
  if (note) {
    note.textContent = ref
      ? `Enquiring about: ${piece} — reference ${ref}`
      : `Enquiring about: ${piece}`;
    note.hidden = false;
  }

  // Give the message box a sensible opening line, without clobbering typing.
  const message = document.querySelector('textarea[name="contact[body]"]');
  if (message && !message.value) {
    message.value = `I would like to enquire about ${piece}${handle ? '' : ''}.`;
  }
})();

/* Enquiry type pre-select.
   Service pages link to the contact form with ?enquiry_type=. Match it to
   an existing option rather than injecting a new one, so a stale or
   mistyped param can never widen the list or submit an unknown value. */
(function () {
  const select = document.querySelector('[data-enquiry-type-field]');
  if (!select) return;

  const wanted = (new URLSearchParams(window.location.search).get('enquiry_type') || '').trim().toLowerCase();
  if (!wanted) return;

  const match = [...select.options].find((o) => o.value.trim().toLowerCase() === wanted
    || o.textContent.trim().toLowerCase() === wanted);
  if (match) select.value = match.value || match.textContent;
})();
